import { ReactNode, useMemo } from 'react';
import { Control, FieldError, FieldPath, FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';

import AsyncSelect, { Option } from '@components/fields/AsyncSelect';
import useAsyncSearch from '@hooks/useAsyncSearch';
import useLazyQueryWithPromise from '@hooks/useLazyQueryWithPromise';
import useMyExpert from '@hooks/useMyExpert';
import { GetExpertsBasicDocument, GetExpertsBasicQuery, GetExpertsBasicQueryVariables } from '@typing/Generated';

type Props<FormData extends FieldValues> = {
  allowAny?: boolean;
  allowMe?: boolean;
  control: Control<FormData>;
  disabled?: boolean;
  error: FieldError | undefined;
  initialOption: {
    label: string | undefined;
    value: string | null;
  };
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  onSelection?: (option: { label: string; value: string }) => void;
  replaceMyExpertWithMe?: boolean;
  required?: boolean;
};

type Expert = NonNullable<NonNullable<GetExpertsBasicQuery['experts']['edges']>[number]>['node'];

const ExpertSelect = <FormData extends FieldValues>({
  allowAny = true,
  allowMe = false,
  control,
  disabled = false,
  error,
  initialOption,
  label,
  name,
  onSelection,
  replaceMyExpertWithMe = false,
  required = false
}: Props<FormData>) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const getExperts = useLazyQueryWithPromise<GetExpertsBasicQuery, GetExpertsBasicQueryVariables>(
    GetExpertsBasicDocument
  );

  const defaultOptions = useMemo(() => {
    const options = [];

    if (allowAny) {
      options.push({ label: intl.formatMessage({ id: 'dictionary.any' }), value: '' });
    }

    if (allowMe && myExpert) {
      options.push({
        label: intl.formatMessage({ id: 'dictionary.me' }),
        value: myExpert?.id ?? ''
      });
    }

    return options;
  }, [allowAny, allowMe, intl, myExpert]);

  const expertAsyncSearch = useAsyncSearch<Expert>({
    defaultOptions,
    excludeValue: allowMe ? myExpert?.id : undefined,
    getLabel: t => t?.fullName ?? '',
    initialOption
  });

  return (
    <AsyncSelect<FormData>
      control={control}
      defaultOptions={expertAsyncSearch.defaultOptions}
      disabled={disabled}
      error={error}
      label={label}
      name={name}
      onSearch={async searchTerm =>
        expertAsyncSearch.searchOptions(searchTerm, getExperts({ variables: { searchTerm } })).then(options =>
          options.map(option => {
            if (replaceMyExpertWithMe && option.value === myExpert?.id) {
              return { ...option, label: intl.formatMessage({ id: 'dictionary.me' }) };
            }
            return option;
          })
        )
      }
      onSelection={(option: Option) => {
        expertAsyncSearch.setCurrentOption(option);
        onSelection?.(option);
      }}
      required={required}
      value={expertAsyncSearch.currentOption}
    />
  );
};

export default ExpertSelect;
